import { useEffect, useState } from "react";
import { api, type DietReport as DietReportT } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";

const fmtDate = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short" });

export default function DietReport() {
  const [data, setData] = useState<DietReportT | null | "error">(null);

  useEffect(() => {
    api.diet.report().then(setData).catch(() => setData("error"));
  }, []);

  const d = data && data !== "error" ? data : null;
  if (data === "error") return <p className="muted">Couldn't load the report.</p>;
  if (!d) return <p className="muted">Loading…</p>;

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">This week</h2>
      <p className="text-ink-subtle text-small mb-4">
        {d.phase ? `${d.phase.name} · ` : ""}since {fmtDate(d.week_start)} — how your logging tracked
        against the plan.
      </p>

      {d.days_logged === 0 ? (
        <EmptyState title="Nothing logged this week yet">
          Log meals (Today tab or photo/voice) and your adherence, on-plan rate and average macros
          show up here.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Days logged" value={`${d.days_logged}`} unit="of 7" accent />
            <Stat label="On plan" value={d.on_plan_pct != null ? `${d.on_plan_pct}` : "—"} unit="%" />
            <Stat
              label="Protein hit"
              value={d.adherence.protein != null ? `${d.adherence.protein}` : "—"}
              unit="% of target"
            />
          </div>

          <div className="card card-pad mt-4">
            <div className="field-label mb-2">Adherence to targets</div>
            <Bar label="Protein" pct={d.adherence.protein} />
            <Bar label="Carbs" pct={d.adherence.carbs} />
            <Bar label="Fat" pct={d.adherence.fat} />
            {d.avg_logged && (
              <p className="mt-2 text-xs text-ink-subtle">
                avg/day logged: {d.avg_logged.protein_g}g P · {d.avg_logged.carbs_g}g C ·{" "}
                {d.avg_logged.fat_g}g F
              </p>
            )}
          </div>
        </>
      )}

      <h3 className="field-label mb-2 mt-8">Adjustment history</h3>
      {d.adjustments.length === 0 ? (
        <div className="card card-pad">
          <p className="muted text-sm">No phase changes yet. The weekly check-in records them here.</p>
        </div>
      ) : (
        <div className="card divide-y divide-ink-line-soft">
          {d.adjustments.map((a, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink">Week of {fmtDate(a.week_start)}</span>
                <span className={a.decision === "keep" ? "pill-neutral" : "pill-warn"}>
                  {a.applied ? "applied" : a.decision}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-ink-subtle">
                {a.from_name && a.to_name && a.from_name !== a.to_name ? `${a.from_name} → ${a.to_name} · ` : ""}
                {a.reason}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={`mt-0.5 font-display text-lg tabular-nums ${accent ? "text-accent" : "text-ink"}`}>{value}</div>
      <div className="text-[10px] text-ink-subtle">{unit}</div>
    </div>
  );
}

function Bar({ label, pct }: { label: string; pct: number | null }) {
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-ink-muted">
        <span>{label}</span>
        <span className="tabular-nums">{pct != null ? `${pct}%` : "—"}</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-ink-line/40">
        <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, pct ?? 0)}%` }} />
      </div>
    </div>
  );
}
